import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { sharesField } from '../../../../../../../data-fields/CommonFields';
import { RendererType } from '../../../../../../../data-models/field-definition';
import { ISelectMeta } from '../../../../../../../data-models/field3.data-model';
import { ProceedsTypes } from '../../../../../../../data-models/transaction.data-model';
import { integerField, usdField } from '../../../../../../../schemas/common-schema-defs';
import { CompanySearchResponseSchema } from '../../../../../../../schemas/CompanySearchResponse.schema';
import { transactionCurrencyIdField } from '../../../../../../../schemas/transactions/TransactionsCommon.schema';
import { fundsByIdMapAtom } from '../../../../../../../services/state/AppConfigStateJ';
import { companyMetricsAtomJ } from '../../../../../../../services/state/CompanyMetricsStateJ';
import { FMT } from '../../../../../../../util/formatter-service';
import { createFormField } from '../../../../../../../view-models/form.view-model';
import {
  getRestructureIdsMeta,
  ITransactionViewModel,
  ITransactionViewModelInternal,
  restructureKeyToFieldKey,
  SellBy,
} from '../../../../../../../view-models/transaction-form.view-model';
import {
  proceedsTypeSchema,
  transactionDateSchema,
} from '../../../../../../Finance2/Forms/transactionFieldSchemas';
import { minZeroMsg, requiredMsg } from '../../utils/validationMessages';
import { eqBuyGridSchema } from './EquityBuySchema';
import { validateRestructureObject } from './EquityShareExchangeSchema';

export function eqSellFields() {
  return {
    acquirer: CompanySearchResponseSchema.formSchema().nullable(),
    splitAcquirerIntoSeparateItem: yup
      .boolean()
      .optional()
      .transform((_, val) => (val ? val : false)),
    fundId: yup.number().nullable().required(requiredMsg('Fund')),
    transactionDate: transactionDateSchema().required(requiredMsg('Transaction Date')),
    valuationImpact: yup
      .boolean()
      .required(requiredMsg('Valuation Impact'))
      .transform((_, val) => (val ? val : false)),
    escrowAmount: usdField()
      .moreThan(-Number.MIN_VALUE, minZeroMsg('Escrow Amount'))
      .nullable()
      .when('proceedsType', {
        is: (val: ProceedsTypes[]) => val?.includes(ProceedsTypes.Cash),
        then: (schema) => schema.required(requiredMsg('Escrow Amount')),
      }),
    noOfShares: integerField()
      .min(0, minZeroMsg('Shares Sold'))
      .nullable()
      .when(['_viewModel.useSplitByRestructureId', '_viewModel.sellBy'], {
        is: (useSplitByRestructureId: boolean, sellBy: SellBy | undefined) =>
          !useSplitByRestructureId && sellBy !== SellBy.returnOfCapitalAmount,
        then: (schema) => schema.required(requiredMsg('Shares Sold')),
      })
      // A value typed before switching modes is hidden, so it must not block submit.
      .when('_viewModel.sellBy', {
        is: SellBy.returnOfCapitalAmount,
        then: (schema) => schema.transform(() => null),
      }),
    returnOfCapitalAmount: usdField()
      .min(0, minZeroMsg('Return of Capital Amount'))
      .nullable()
      .when('_viewModel.sellBy', {
        is: SellBy.returnOfCapitalAmount,
        then: (schema) => schema.required(requiredMsg('Return of Capital Amount')),
        otherwise: (schema) => schema.transform(() => null),
      }),
    notes: yup
      .string()
      .optional()
      .nullable()
      .transform((_, val) => (val === val ? val : null)),
    currencyid: transactionCurrencyIdField(),
    proceedsType: proceedsTypeSchema(),
    sharesReceived: integerField()
      .optional()
      .nullable()
      .when('proceedsType', {
        is: (val: ProceedsTypes[]) => val?.includes(ProceedsTypes.StockProceeds),
        then: (schema) => schema.required(requiredMsg('Shares Received from Acquirer')),
      }),
    ppsSharesReceived: usdField()
      .optional()
      .nullable()
      .label('PPS of Shares Received')
      .when('proceedsType', {
        is: (val: ProceedsTypes[]) => val?.includes(ProceedsTypes.StockProceeds),
        then: (schema) => schema.required(),
      }),
    receivedAmount: usdField()
      .optional()
      .nullable()
      .when('proceedsType', {
        is: (val: ProceedsTypes[]) => val?.includes(ProceedsTypes.Cash),
        then: (schema) => schema.nonNullable().required(requiredMsg('Cash Received')),
      }),
    fmvBasis: yup.string().optional().nullable(),
    _viewModel: yup.object({
      exchangedSharesByRestructureId: yup
        .object()
        .nullable()
        .test((value, context) => {
          if (
            !context?.parent?.useSplitByRestructureId ||
            context?.parent?.sellBy === SellBy.returnOfCapitalAmount
          ) {
            return true;
          }

          return validateRestructureObject(
            value,
            '_viewModel.exchangedSharesByRestructureId',
            'All securities must have a shares value'
          );
        }),
    }),
  };
}

export function eqSellFormSchema() {
  return yup.object().shape(eqSellFields());
}

export function eqSellGridSchema() {
  const base = eqSellFields();
  base.acquirer = CompanySearchResponseSchema.gridSchema();
  return yup
    .object(base)
    .concat(eqBuyGridSchema())
    .shape({
      ...base,
      noOfShares: base.noOfShares.gridMeta({
        label: 'Shares Sold',
        renderer: RendererType.number,
      }),
      returnOfCapitalAmount: base.returnOfCapitalAmount.gridMeta({
        label: 'Return of Capital Amount',
        renderer: RendererType.number,
      }),
    })
    .omit(['fmvBasis', 'fullyDilutedShares', 'pricePerShare', '_viewModel']);
}

export function useSharesSoldField(companyId: number) {
  const metrics = useAtomValue(companyMetricsAtomJ({ companyId }));
  const fundsMap = useAtomValue(fundsByIdMapAtom);
  const selectedDate = useWatch<ITransactionViewModel>({ name: 'transactionDate' });
  const selectedFundId = useWatch<ITransactionViewModel>({ name: 'fundId' }) as number | undefined;
  const selectedSecurities = useWatch<ITransactionViewModel>({
    name: '_viewModel.selectedSecurityForSharesSold',
  }) as ITransactionViewModelInternal['selectedSecurityForSharesSold'];
  const showSharesBySecurity = useWatch<ITransactionViewModel>({
    name: '_viewModel.useSplitByRestructureId',
  });

  return useCallback(() => {
    if (showSharesBySecurity) {
      const latestRestructureTransactions = getRestructureIdsMeta(
        metrics?.metrics[0]?.transactions ?? [],
        selectedFundId ?? -1,
        new Date(selectedDate as string)
      );

      const restructureSelectField = createFormField<ISelectMeta<string>>({
        key: `_viewModel.selectedSecurityForSharesSold`,
        label: 'Select Securities',
        description: 'Select the security for which you want to enter shares sold',
        required: true,
        renderer: RendererType.multiSelect,
        rendererMeta: {
          values: Array.from(latestRestructureTransactions.values()).map((restructureMeta) => {
            const { fundId, position, restructureId, transactionDate } = restructureMeta;
            const label = position ?? FMT.format('dateNumeric', transactionDate);
            const fundLabel =
              fundId && fundsMap.get(fundId)?.name ? `Fund: ${fundsMap.get(fundId)?.name}, ` : '';
            const description = `${fundLabel}Acquired Date: ${FMT.format('dateNumeric', transactionDate)}`;

            return {
              value: restructureId,
              displayName: label,
              description,
            };
          }),
        },
        disableClearable: true,
      });

      const restructureFields = Array.from(selectedSecurities ?? []).map((restructureId) => {
        const { fundId, position, transactionDate } = latestRestructureTransactions.get(restructureId)!;
        const label = position ?? FMT.format('dateNumeric', transactionDate);
        const fundLabel = fundId && fundsMap.get(fundId)?.name ? `Fund: ${fundsMap.get(fundId)?.name}, ` : '';
        const description = `${fundLabel}Acquired Date: ${FMT.format('dateNumeric', transactionDate)}`;

        return sharesField({
          key: `_viewModel.exchangedSharesByRestructureId.${restructureKeyToFieldKey(restructureId ?? '')}`,
          label,
          description,
          required: true,
        });
      });

      return [restructureSelectField, ...restructureFields];
    } else {
      return [sharesField({ key: 'noOfShares', label: 'Shares Sold', required: true })];
    }
  }, [fundsMap, metrics?.metrics, selectedSecurities, showSharesBySecurity, selectedDate, selectedFundId]);
}
